import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import FileInput from "components/forms/FileInput";
import styles from "./styles.module.scss";

import Header from "components/ui/Header";
import Page from "components/ui/Page";
import BasicButton from "components/forms/BasicButton";
import MessageEditor from "components/forms/MessageEditor";
import LeadPicker from "containers/Leads/LeadPicker";
import { getClients } from "api/Clients";
import { createLead, getLeadSettings, getLead } from "api/Leads";
import { getMoProposalPrefill } from "api/MaintenanceOpportunities";

import {
  parseLocationList,
  geocodeRows,
  getProposalCatalog,
  getClientSites,
  getCompletedProjectSites,
  getCaseStudies,
  type CaseStudy,
  getCachedContacts,
  enrichSites,
  type EnrichedSite,
  previewProposal,
  getSectionDefaults,
  previewProposalHtml,
  createProposal,
  updateProposal,
  getProposal,
  sendProposal,
  setProposalPassword,
  getProposalSenders,
  type ProposalSender,
  type BuildInput,
  type CustomerType,
  type ParsedRow,
  type ParseSummary,
  type AddressDuplicate,
  type OfferType,
  type FacilityType,
  type AssumptionDef,
  type PreviewWarnings,
  type ProposalPayload,
  type SectionId,
  type SiteInput,
} from "api/Proposals";

/**
 * Proposal wizard — four steps, defaults everywhere.
 *
 * The target is that the fast path is Next -> Next -> Next -> Send, and that
 * nobody has to know what a p90 percentile is to get a proposal out. Every
 * step has a working computed default; the operator only touches what they
 * disagree with.
 *
 * Pricing is never held client-side. Step 3 re-runs `preview` on the server
 * whenever the offer changes, so the figures on screen are always the current
 * rate card applied to the actual sites in the list.
 */

const TYPES: Array<{
  key: CustomerType;
  title: string;
  body: string;
  eg: string;
}> = [
  {
    key: "multi_location_brand",
    title: "A multi-location brand",
    body: "A chain we don't work with yet. We build the location list ourselves and use company-wide performance numbers as proof.",
    eg: "Childcare Network · Kiddie Academy · KOA · YMCA",
  },
  {
    key: "existing_client_portfolio",
    title: "An existing client — going after the rest",
    body: "They already use us for some locations. The proposal uses their own results with us as proof, then prices the portfolio they haven't given us.",
    eg: "EMCOR / LCG · Kompan",
  },
  {
    key: "single_location",
    title: "A single-location business",
    body: "One site, one owner, found by the sales team. No ladder, no map — pricing for their site and the annual plan that keeps it compliant.",
    eg: "An independent daycare · a church · one KOA park",
  },
  {
    key: "completed_site",
    title: "A site we've already mulched",
    body: "A playground builder brought us the job. We know the site but not who owns it. The ask is an annual maintenance plan, not a national contract.",
    eg: "Poinciana Village Park · Timber Ridge",
  },
];

/**
 * Only the sections an operator can meaningfully toggle, and only the ones
 * that belong to the chosen customer type. Showing "dig-out services" on a
 * single-playground proposal invites someone to turn on a section that has
 * nothing to attach to.
 */
const PORTFOLIO_SECTIONS: SectionId[] = [
  "savings_ladder",
  "regional_pricing",
  "regional_map",
  "digouts",
  "admin_time_value",
  "program_value",
  "hidden_cost",
  "execution_speed",
  "emergency_response",
  "benefits",
];

const MAINTENANCE_SECTIONS: SectionId[] = [
  "site_history",
  "why_maintenance",
  "annual_plan",
  "compliance",
  "how_it_works",
  "plan_benefits",
  "execution_speed",
  "emergency_response",
];

const SECTION_LABELS: Partial<Record<SectionId, { label: string; note: string }>> = {
  admin_time_value: { label: "Admin time value", note: "Hours the client's team gets back, priced at a labour rate. Both are assumptions." },
  program_value: { label: "Total program value", note: "Pricing savings + admin time value. Follows the two sections above." },
  regional_pricing: { label: "Regional pricing table", note: "Rates rolled up from each site's own resolved price." },
  regional_map: { label: "Regional map", note: "US choropleth, coloured by region." },
  execution_speed: { label: "Execution speed", note: "Measured from completed work, not typed in." },
  hidden_cost: { label: "The hidden cost of managing it yourself", note: "Used on cold brands who still run this in-house." },
  emergency_response: { label: "Emergency & storm response", note: "Needs an approved case study — pick one below." },
  digouts: { label: "Dig-out services", note: "Unlocks per commitment level, not on a global threshold." },
  savings_ladder: { label: "Commitment levels", note: "Off automatically below 40 locations." },
  site_history: { label: "What we did here before", note: "Only for a site we have already mulched." },
  why_maintenance: { label: "Why surfacing needs maintenance", note: "The depth argument — the core of a plan proposal." },
  annual_plan: { label: "The annual plan and cost", note: "One site, one number, a cadence. No ladder." },
  compliance: { label: "Documentation", note: "What they can hand to an inspector." },
  how_it_works: { label: "How it works", note: "Three steps, no contract." },
  plan_benefits: { label: "What the plan includes", note: "The single-site version of the benefits grid." },
};

type InputMode = "paste" | "csv" | "ours";

/**
 * The other two questions that define a proposal type. Step 1 answers "who
 * are they to us"; these answer what we are offering and for what kind of
 * facilities. Size is never asked - it comes from the list.
 */
const OFFER_OPTIONS: Array<{ value: OfferType; label: string; note: string }> = [
  { value: "commitment_program", label: "Volume commitment program", note: "Levels, regional pricing, map, program value. 40+ sites." },
  { value: "priced_list", label: "Priced site list", note: "Every site priced, no commitment levels. 2–39 sites." },
  { value: "single_job", label: "One job at one site", note: "One price, one site." },
  { value: "top_up", label: "Top-up of past work", note: "A refresh at a site we have already done." },
  { value: "maintenance_plan", label: "Maintenance plan", note: "An annual plan for one site." },
];

const FACILITY_OPTIONS: Array<{ value: FacilityType; label: string }> = [
  { value: "childcare", label: "Childcare & preschool" },
  { value: "k12", label: "K-12 school district" },
  { value: "parks_municipal", label: "Parks & municipal" },
  { value: "hoa_multifamily", label: "HOA & multifamily" },
  { value: "church_camp", label: "Church & camp" },
  { value: "other", label: "Other" },
  // Different story, same pricing (docs/PROPOSAL_AUDIENCES.md).
  { value: "developer_playground", label: "Developer - Playground" },
  { value: "developer_generic", label: "Developer - Generic" },
  { value: "non_playground", label: "Non-Playground" },
];

const SOURCE_LABELS: Record<string, string> = {
  measured: "Measured",
  list: "From the list",
  default: "Default",
  operator: "Set by you",
};

const Proposals = () => {
  const { id: editId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Step 1
  const [customerType, setCustomerType] = useState<CustomerType>("multi_location_brand");
  const [prospectName, setProspectName] = useState("");
  const [clientId, setClientId] = useState("");
  const [productId, setProductId] = useState("");
  const [serviceId, setServiceId] = useState("");
  const [catalog, setCatalog] = useState<{
    products: Array<{ id: string; name: string; unit: string | null }>;
    services: Array<{ id: string; name: string; depots: number }>;
  }>({ products: [], services: [] });
  const [clients, setClients] = useState<Array<{ id: string; name: string }>>([]);
  /**
   * LEADS (LEADS_MODULE_SCOPE.md s.6). A proposal for a prospect that is
   * not a client is attached to a LEAD - a client row in stage = lead - so
   * its opens, replies and follow-ups land on the lead's Activity instead
   * of floating free. Three ways in: `?leadId=` from the lead record, an
   * existing open lead picked here, or a new lead minted from the prospect
   * name when leaving step 1. Only offered when the module is switched on.
   */
  const [leadsEnabled, setLeadsEnabled] = useState(false);
  const [leadId, setLeadId] = useState<string>(searchParams.get("leadId") ?? "");
  const [createLeadOnNext, setCreateLeadOnNext] = useState(false);
  useEffect(() => {
    getLeadSettings().then(async (s) => {
      setLeadsEnabled(!!s?.enabled);
      if (!s?.enabled) return;
      // `?leadId=` from the lead record: fetched directly. It used to be looked
      // up in the first 100 leads by name, so with 212 leads most were never
      // found and the proposal silently stayed unattached (2026-09-28).
      const fromUrl = searchParams.get("leadId");
      if (!fromUrl) return;
      const l: any = await getLead(fromUrl);
      if (l && !l.error) {
        setLeadId(l.id);
        setClientId(l.id);
        setProspectName((p) => p || l.name);
      }
    });
  }, []); // eslint-disable-line

  // Step 2
  const [mode, setMode] = useState<InputMode>("paste");
  const [pasteText, setPasteText] = useState("");
  const [rows, setRows] = useState<ParsedRow[]>([]);
  const [summary, setSummary] = useState<ParseSummary | null>(null);
  // Address-level merges from the last parse. Kept apart from `summary`
  // because a geocode run replaces the summary and the notice must stay.
  const [duplicates, setDuplicates] = useState<AddressDuplicate[]>([]);
  const [showDuplicates, setShowDuplicates] = useState(false);
  const [geocodeProgress, setGeocodeProgress] = useState<string | null>(null);
  // The parsed-rows table pages rather than truncating: at 258 sites a
  // 25-row cut hid most of the flagged rows the table exists to show.
  const ROWS_PER_PAGE = 25;
  const [rowsPage, setRowsPage] = useState(0);
  const [candPage, setCandPage] = useState(0);
  useEffect(() => {
    setRowsPage(0);
  }, [rows]);
  /**
   * Everything the search or paste turned up. SELECTION IS SEPARATE, because
   * a search that returns 40 completed projects is a shortlist, not a
   * decision — previously every row it found went into the proposal whether
   * or not it belonged there.
   */
  const [candidates, setCandidates] = useState<SiteInput[]>([]);
  const [selectedRefs, setSelectedRefs] = useState<Set<string>>(new Set());
  useEffect(() => {
    setCandPage(0);
  }, [candidates.length]);
  // Once every row is placed, the parsed-rows table only repeats the
  // selection list above it - so it shows just the rows needing attention.
  const tableRows = summary && summary.needGeocode === 0 ? rows.filter((r) => r.flags.length > 0) : rows;
  const [contacts, setContacts] = useState<Record<string, EnrichedSite>>({});

  // Step 3
  const [sections, setSections] = useState<Partial<Record<SectionId, boolean>>>({});
  // True once the operator flips a toggle: from then on a preview never
  // re-seeds the sections over their choices.
  const sectionsTouched = React.useRef(false);
  // Proposal type, the other two questions. Offer is undefined until the
  // first preview suggests one from the site count; the operator can change it.
  const [offerType, setOfferType] = useState<OfferType | undefined>(undefined);

  /**
   * Toggles right away on arriving at step 3 (2026-09-28). The first
   * /preview reprices every site and took 10+ s, and until it returned
   * every toggle rendered OFF, which read as "nothing is included". These
   * are the same defaults, answered without pricing.
   */
  const seedSections = async (siteCount: number) => {
    if (sectionsTouched.current) return;
    const d = await getSectionDefaults({ customerType, offerType: offerType ?? null, siteCount });
    if (d?.sections && !sectionsTouched.current) setSections((cur) => (Object.keys(cur).length ? cur : (d.sections as any)));
  };
  // Who the proposal is FROM. Defaults to the current user once the sender
  // list loads; the contact block on the document is built from this.
  const [senders, setSenders] = useState<ProposalSender[]>([]);
  const [senderUserId, setSenderUserId] = useState<string>("");
  const [prospectLogoUrl, setProspectLogoUrl] = useState<string>("");
  // A local preview of a just-uploaded logo (the stored value is a private key).
  const [logoPreview, setLogoPreview] = useState<string>("");
  const [logoBusy, setLogoBusy] = useState(false);
  useEffect(() => {
    getProposalSenders().then(setSenders);
  }, []);
  const [facilityType, setFacilityType] = useState<FacilityType>("childcare");
  // Developers build and hand over; a single-site maintenance plan or top-up
  // is an owner's offer. On a single or completed site the developer types are
  // not offered, and one already picked moves to its owner equivalent
  // (docs/PROPOSAL_TYPE_AUDIT.md item 5).
  const singleSiteCustomer = customerType === "single_location" || customerType === "completed_site";
  const facilityOptions = singleSiteCustomer
    ? FACILITY_OPTIONS.filter((f) => f.value !== "developer_playground" && f.value !== "developer_generic")
    : FACILITY_OPTIONS;
  useEffect(() => {
    if (!singleSiteCustomer) return;
    if (facilityType === "developer_playground") setFacilityType("other");
    else if (facilityType === "developer_generic") setFacilityType("non_playground");
  }, [singleSiteCustomer, facilityType]);
  // Operator-set assumptions, by key. Everything else keeps its resolved value.
  const [assumptionOverrides, setAssumptionOverrides] = useState<Record<string, { value: number | string; reason?: string }>>({});
  const [assumptionDefs, setAssumptionDefs] = useState<Partial<Record<SectionId, AssumptionDef[]>>>({});
  // Draft edits before they are committed (a number typed, a reason being written).
  const [assumptionDraft, setAssumptionDraft] = useState<{ key: string; value: string; reason: string } | null>(null);
  const [caseStudies, setCaseStudies] = useState<CaseStudy[]>([]);
  const [caseStudyId, setCaseStudyId] = useState<string>("");
  const [ladderLevelCount, setLadderLevelCount] = useState(4);
  const [ladderEnabled, setLadderEnabled] = useState<boolean | undefined>(undefined);
  const [ladderLevels, setLadderLevels] = useState<any[] | undefined>(undefined);
  const [payload, setPayload] = useState<ProposalPayload | null>(null);
  const [warnings, setWarnings] = useState<PreviewWarnings | null>(null);

  // Step 4
  const [html, setHtml] = useState<string>("");
  const [sentTo, setSentTo] = useState("");
  const [subject, setSubject] = useState("");
  const [messageBody, setMessageBody] = useState("");

  /**
   * The attached lead's person, for the Send step (2026-09-28): To = their
   * email and the message opens "Hi <first name>," - only while those are
   * still empty, so nothing the operator typed is overwritten.
   */
  useEffect(() => {
    if (!leadId) return;
    let cancelled = false;
    getLead(leadId).then((l: any) => {
      if (cancelled || !l || l.error) return;
      if (l.contactEmail) setSentTo((cur) => cur || l.contactEmail);
      const first = String(l.contactName ?? "").trim().split(/\s+/)[0];
      if (first) setMessageBody((cur) => cur || `Hi ${first},\n\n`);
    });
    return () => {
      cancelled = true;
    };
  }, [leadId]);
  const [includePassword, setIncludePassword] = useState(true);
  // The link's password is the operator's call: on with the generated one,
  // on with their own, or off. Editable after the link exists.
  const [requirePassword, setRequirePassword] = useState(true);
  const [customPassword, setCustomPassword] = useState("");
  const [linkPassword, setLinkPassword] = useState<string | null>(null);
  const [passwordDraft, setPasswordDraft] = useState<string | null>(null);
  const [savedId, setSavedId] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [emailed, setEmailed] = useState<boolean | null>(null);

  /**
   * What actually goes into the proposal: the candidates the operator ticked.
   * Declared before every effect that reads it, so nothing touches it in the
   * temporal dead zone.
   */
  const sites = useMemo(
    () => candidates.filter((c) => selectedRefs.has(c.ref)),
    [candidates, selectedRefs],
  );

  /**
   * Sites ACCUMULATE across input modes rather than replacing each other.
   *
   * The real case is "their 40 known sites, plus 12 new ones they emailed me"
   * — so loading from our data and then pasting a list adds to the set, and
   * the selection already made survives. Replacing was simpler and wrong.
   *
   * Deduped on ref, because selection is keyed on it: two rows sharing one ref
   * tick and untick together and neither can be cleared, which is exactly what
   * happened when a location with several completed projects came back once
   * per project.
   */
  const addCandidates = (list: SiteInput[], selectNew: boolean) => {
    const added: SiteInput[] = [];
    setCandidates((prev) => {
      const seen = new Set(prev.map((s) => s.ref));
      for (const s of list) {
        if (seen.has(s.ref)) continue;
        seen.add(s.ref);
        added.push(s);
      }
      return [...prev, ...added];
    });
    if (selectNew) {
      setSelectedRefs((sel) => {
        const next = new Set(sel);
        added.forEach((s) => next.add(s.ref));
        return next;
      });
    }
  };

  const clearCandidates = () => {
    setCandidates([]);
    setSelectedRefs(new Set());
    setRows([]);
    setSummary(null);
    setDuplicates([]);
  };

  /**
   * `?opportunityId=` from Leads > Maintenance opportunities (or the project
   * card): open pre-filled - the lead, a completed site, a maintenance-plan
   * offer, the owner's facility type, and the one site already ticked. Sets
   * candidates/selection directly rather than through addCandidates, whose
   * selectNew reads a list filled inside a state updater.
   */
  useEffect(() => {
    const oppId = searchParams.get("opportunityId");
    if (!oppId) return;
    getMoProposalPrefill(oppId).then((p: any) => {
      if (!p || p.error) return setError(p?.error ?? "Could not load the opportunity.");
      setCustomerType("completed_site");
      setLeadId(p.leadId);
      setClientId(p.leadId);
      setProspectName((cur) => cur || p.prospectName || "");
      if (p.offerType) setOfferType(p.offerType);
      if (p.facilityType) setFacilityType(p.facilityType);
      if (p.site?.ref && p.site.latitude != null && p.site.longitude != null) {
        const site: any = { ...p.site };
        setCandidates((prev) => (prev.some((s) => s.ref === site.ref) ? prev : [...prev, site]));
        setSelectedRefs((sel) => new Set(sel).add(site.ref));
      }
    });
  }, []); // eslint-disable-line

  /**
   * Replace only the rows that came from one input mode.
   *
   * Ref prefixes keep the modes apart: `row-` pasted, `csv-` uploaded, a bare
   * uuid from our own data. Re-parsing or geocoding a pasted list has to
   * REPLACE those rows (their coordinates change) without touching sites
   * loaded from the database alongside them.
   */
  const replaceFromMode = (prefix: string, list: SiteInput[], selectNew: boolean) => {
    setCandidates((prev) => {
      const kept = prev.filter((s) => !s.ref.startsWith(prefix));
      const seen = new Set(kept.map((s) => s.ref));
      const added = list.filter((s) => {
        if (seen.has(s.ref)) return false;
        seen.add(s.ref);
        return true;
      });
      return [...kept, ...added];
    });
    setSelectedRefs((sel) => {
      const next = new Set(Array.from(sel).filter((r) => !r.startsWith(prefix)));
      if (selectNew) list.forEach((s) => next.add(s.ref));
      return next;
    });
  };

  /** Replaces everything. Only used when opening an existing draft. */
  const setCandidatesAndSelect = (list: SiteInput[], selectAll: boolean) => {
    const seen = new Set<string>();
    const unique = list.filter((s) => {
      if (seen.has(s.ref)) return false;
      seen.add(s.ref);
      return true;
    });
    setCandidates(unique);
    setSelectedRefs(selectAll ? new Set(unique.map((s) => s.ref)) : new Set());
  };

  const toggleSite = (ref: string) => {
    setSelectedRefs((prev) => {
      const next = new Set(prev);
      if (next.has(ref)) next.delete(ref);
      else next.add(ref);
      return next;
    });
  };

  /**
   * Selected sites with no contact on file — the only ones a lookup would
   * bill for. Computed here rather than asked of the server on every tick:
   * the cost is arithmetic, and a round trip per checkbox to learn it would
   * be worse than the lookup itself.
   */
  const billableSites = useMemo(
    () => sites.filter((s) => !contacts[s.ref]?.endCustomerName),
    [sites, contacts],
  );

  useEffect(() => {
    document.title = process.env.REACT_APP_NAME + " | Proposals";
    getProposalCatalog().then((res: any) => {
      if (res?.error) return;
      setCatalog(res);
      // Preselect the only sensible default rather than making the operator
      // pick from a list where one option covers almost every proposal.
      const mulch = res.products.find((p: any) => /playground mulch/i.test(p.name));
      if (mulch) setProductId(mulch.id);
      const install = res.services.find((s: any) => /installation of mulch/i.test(s.name));
      if (install) setServiceId(install.id);
    });

    // Clients for the picker. Typing a UUID was never a real input — the app
    // holds the list, so the operator should be choosing from it.
    getClients({
      query: { recordsPer: 500, pageNum: 1, sortBy: "name", sortDir: "asc" },
    }).then((res: any) => {
      const items = Array.isArray(res?.items) ? res.items : Array.isArray(res) ? res : [];
      setClients(items.map((c: any) => ({ id: c.id, name: c.name })));
    });

    getCaseStudies().then((res: any) => {
      if (!res?.error) setCaseStudies((res.items ?? []).filter((c: CaseStudy) => c.approved));
    });
  }, []);

  /**
   * Opening an existing DRAFT — either one saved earlier or one produced by
   * regenerating a sent proposal.
   *
   * The sites carry over; the prices deliberately do not. Everything jumps
   * straight to the offer screen, which reprices against the CURRENT rate card
   * on arrival. That is the whole point of reopening a draft rather than
   * reading a stored copy of it.
   */
  useEffect(() => {
    if (!editId) return;
    setBusy(true);
    getProposal(editId).then((res: any) => {
      setBusy(false);
      if (res?.error) return setError(res.error);
      if (res.status !== "draft") {
        return setError(
          "This proposal has already been sent and cannot be edited. Regenerate it as a new version instead."
        );
      }

      setCustomerType(res.customerType);
      setProspectName(res.prospectName);
      setSenderUserId(res.sender?.id ?? "");
      setProspectLogoUrl(res.prospectLogoUrl ?? "");
      setClientId(res.client?.id ?? "");
      // A draft attached to a lead reopens attached (the select shows it
      // when the id is a lead; a real client id just leaves it unmatched).
      setLeadId(res.client?.id ?? "");
      setProductId(res.productId ?? "");
      setServiceId(res.serviceId ?? "");
      setSavedId(res.id);

      const loaded: SiteInput[] = (res.locations ?? [])
        .filter((l: any) => l.latitude != null && l.longitude != null)
        .map((l: any) => ({
          ref: l.sourceLocation?.id ?? l.id,
          name: l.name ?? undefined,
          state: l.state ?? undefined,
          latitude: Number(l.latitude),
          longitude: Number(l.longitude),
          yards: l.yards != null ? Number(l.yards) : undefined,
        }));
      // A saved draft already IS the selection someone made, so everything on
      // it comes back ticked.
      setCandidatesAndSelect(loaded, true);
      setSummary({
        total: loaded.length,
        withCoordinates: loaded.length,
        needGeocode: 0,
        withYards: loaded.filter((s) => !!s.yards).length,
        flagged: 0,
      });
      setStep(3);
    });
    // eslint-disable-line
  }, [editId]);

  // Input mode follows the customer type rather than asking again: a cold
  // brand has no rows in our data, an existing client does.
  useEffect(() => {
    setMode(
      customerType === "multi_location_brand" ? "paste" : "ours"
    );
  }, [customerType]);

  // A loaded draft lands on step 3 with no payload yet, so price it as soon
  // as the sites are in place.
  useEffect(() => {
    if (editId && step === 3 && sites.length > 0 && !payload && productId && serviceId) {
      runPreview();
    }
  }, [editId, step, sites.length, payload, productId, serviceId]); // eslint-disable-line

  /**
   * Persist without sending.
   *
   * The wizard used to save ONLY as part of sending, so a completed proposal
   * that was not sent existed nowhere — a 189-row CSV import, the geocoding
   * that resolved it and every selection made on top of it, all gone on
   * navigate-away. Importing is the expensive part of this flow and it was the
   * part with no durability.
   */
  const saveDraftOnly = async (silent = false): Promise<string | null> => {
    if (!canLeaveStep1 || sites.length === 0) return null;
    if (!silent) setBusy(true);
    setError(null);

    const res: any = savedId
      ? await updateProposal(savedId, buildInput)
      : await createProposal(buildInput);

    if (!silent) setBusy(false);
    if (res?.error) {
      setError(res.error);
      return null;
    }

    const id = res.id ?? savedId;
    setSavedId(id);
    setSavedAt(new Date());
    return id;
  };

  /**
   * Save once on arrival at step 3, the moment the location work is done.
   * Silent, because the operator did not ask for it — they just should not be
   * able to lose an import by closing a tab.
   */
  useEffect(() => {
    if (step === 3 && !savedId && sites.length > 0 && canLeaveStep1) {
      saveDraftOnly(true);
    }
    // eslint-disable-line
  }, [step]); // eslint-disable-line

  const buildInput = useMemo<BuildInput>(
    () => ({
      customerType,
      prospectName,
      clientId: clientId || null,
      productId,
      serviceId,
      sites,
      sections,
      offerType,
      facilityType,
      assumptions: assumptionOverrides,
      senderUserId: senderUserId || undefined,
      prospectLogoUrl: prospectLogoUrl || null,
      caseStudyId: caseStudyId || null,
      ladderLevelCount,
      ladderEnabled,
      ladderLevels,
      maintenanceOpportunityId: searchParams.get("opportunityId") || null,
    }),
    [customerType, prospectName, clientId, productId, serviceId, sites, sections, offerType, facilityType, assumptionOverrides, senderUserId, prospectLogoUrl, caseStudyId, ladderLevelCount, ladderEnabled, ladderLevels]
  );

  /* ---------------------------------------------------------------- step 2 */

  const runParse = async () => {
    setBusy(true);
    setError(null);
    const res: any = await parseLocationList({ text: pasteText });
    setBusy(false);
    if (res?.error) return setError(res.error);
    setRows(res.rows);
    setSummary(res.summary);
    setDuplicates(res.duplicates ?? []);
    // A pasted list is an explicit list — everything on it is selected. It
    // REPLACES previously pasted rows but leaves sites loaded from our data
    // alone, so the two can be combined in one proposal.
    replaceFromMode(
      "row-",
      res.rows
        .filter((r: ParsedRow) => r.latitude != null && r.longitude != null)
        .map((r: ParsedRow, i: number) => ({
          ref: `row-${r.sourceRow}-${i}`,
          name: r.name,
          state: r.state,
          latitude: r.latitude as number,
          longitude: r.longitude as number,
          yards: r.yards ?? undefined,
        })),
      true,
    );
  };

  const applyGeocoded = (next: ParsedRow[]) => {
    setRows(next);
    setSummary((prev) => ({
      ...(prev ?? {}),
      total: next.length,
      withCoordinates: next.filter((r: ParsedRow) => r.latitude != null).length,
      needGeocode: next.filter((r: ParsedRow) => r.latitude == null).length,
      withYards: next.filter((r: ParsedRow) => !!r.yards).length,
      flagged: next.filter((r: ParsedRow) => r.flags.length > 0).length,
    }));
    // The prefix of the mode the rows came from. This used "row-" (the
    // paste prefix) for CSV rows too, so after a geocode the CSV's already-
    // located sites sat in the list twice: once as "csv-", once as "row-".
    const prefix = mode === "csv" ? "csv-" : "row-";
    replaceFromMode(
      prefix,
      next
        .filter((r: ParsedRow) => r.latitude != null && r.longitude != null)
        .map((r: ParsedRow, i: number) => ({
          ref: `${prefix}${r.sourceRow}-${i}`,
          name: r.name,
          state: r.state,
          latitude: r.latitude as number,
          longitude: r.longitude as number,
          yards: r.yards ?? undefined,
        })),
      true,
    );
  };

  /**
   * BATCHED. One request per 50 unresolved rows, looping until none are
   * left. A single request for a 258-site list finished on the server but
   * outran the gateway's ~30 s limit, so the browser got a 503 and the
   * answer was lost (production, 2026-09-23). Each batch's result is kept
   * as it lands, so a failure part-way loses only that batch, and pressing
   * the button again carries on from where it stopped.
   */
  const runGeocode = async () => {
    setBusy(true);
    setError(null);
    // A fresh press retries rows an earlier run gave up on.
    let current: ParsedRow[] = rows.map((r) => ({
      ...r,
      flags: r.flags.filter((f) => f !== "geocode_failed"),
    }));
    const total = current.filter((r) => r.latitude == null || r.longitude == null).length;
    let done = 0;
    setGeocodeProgress(total ? `0 of ${total}` : null);

    for (let i = 0; i < 40; i++) {
      const res: any = await geocodeRows(current, 50);
      if (res?.error) {
        setError(`${res.error} ${done} of ${total} looked up; press the button again to carry on.`);
        break;
      }
      if (res.unavailable) {
        setError("Geocoding is not configured in this environment.");
        break;
      }
      current = res.rows;
      applyGeocoded(current);
      done = total - (res.remaining ?? 0);
      setGeocodeProgress(`${done} of ${total}`);
      if (res.capped === "per_day") {
        setError(
          `Daily geocoding limit reached \u2014 ${done} of ${total} looked up, the rest are still waiting. Try again tomorrow or trim the list.`
        );
        break;
      }
      if (!res.remaining) break;
    }

    setBusy(false);
    setGeocodeProgress(null);
  };

  const loadFromOurData = async () => {
    setBusy(true);
    setError(null);
    const res: any =
      customerType === "existing_client_portfolio"
        ? await getClientSites(clientId)
        : await getCompletedProjectSites({ minYards: 100, monthsBack: 24, limit: 200 });
    setBusy(false);
    if (res?.error) return setError(res.error);
    // FOUND, NOT CHOSEN. An existing client's portfolio is the thing being
    // proposed, so it arrives ticked; a completed-project search is a
    // shortlist to pick from, so it arrives empty. Including all 40 results
    // because the search returned them is how a proposal goes out covering
    // sites nobody meant to include.
    //
    // ADDS rather than replaces, so a client's known sites and a pasted list
    // of new ones can go in the same proposal.
    addCandidates(res.sites, customerType === "existing_client_portfolio");
    setSummary({
      total: res.sites.length,
      withCoordinates: res.sites.length,
      needGeocode: 0,
      withYards: res.sites.filter((s: SiteInput) => !!s.yards).length,
      flagged: 0,
    });

    // Cached answers are free, so they load without asking. Only the sites
    // nobody has resolved yet cost anything, and that number goes on a button.
    const cached: any = await getCachedContacts(res.sites.map((s: SiteInput) => s.ref));
    if (!cached?.error) {
      const map: Record<string, EnrichedSite> = {};
      for (const c of cached.sites) if (c.endCustomerName) map[c.locationId] = c;
      setContacts(map);
    }
  };

  const enrichTargetsFor = (list: SiteInput[]) =>
    list.map((s) => ({
      locationId: s.ref,
      projectName: s.name,
      latitude: s.latitude,
      longitude: s.longitude,
    }));

  const runEnrichment = async () => {
    setBusy(true);
    setError(null);
    // Only what is selected and unresolved. Paying to identify sites that are
    // not going in the proposal is the cost problem this screen exists to fix.
    const res: any = await enrichSites(enrichTargetsFor(billableSites));
    setBusy(false);
    if (res?.error) return setError(res.error);
    if (res.unavailable) return setError("Contact lookup is not configured in this environment.");

    const map: Record<string, EnrichedSite> = { ...contacts };
    for (const c of res.sites) if (c.endCustomerName) map[c.locationId] = c;
    setContacts(map);

    if (res.capped) {
      setError(
        res.capped === "per_day"
          ? "Daily lookup limit reached. The rest are still unresolved."
          : "Stopped at the per-run limit. Run it again for the rest."
      );
    }
  };

  /* ---------------------------------------------------------------- step 3 */

  const runPreview = async (override?: Partial<BuildInput>) => {
    setBusy(true);
    setError(null);
    const res: any = await previewProposal({ ...buildInput, ...(override ?? {}) });
    setBusy(false);
    if (res?.error) return setError(res.error);
    setPayload(res.payload);
    setWarnings(res.warnings);
    if (res.assumptionDefs) setAssumptionDefs(res.assumptionDefs);
    if (!offerType && res.payload?.offerType && !override?.offerType) setOfferType(res.payload.offerType);
    // First preview seeds the toggles from the computed defaults (it knows
    // the PRICED site count, which can change the suggested offer type), as
    // long as the operator has not changed one yet.
    if (!sectionsTouched.current || override?.offerType) setSections(res.payload.sections);
    if (ladderEnabled === undefined) setLadderEnabled(res.payload.ladder.enabled);
    if (!ladderLevels) setLadderLevels(res.payload.ladder.levels);
  };

  /**
   * Ladder edits repreview on blur, not on keystroke: every preview is a
   * live repricing round trip, and firing one per character would hammer the
   * pricing path for no benefit.
   */
  const editLevel = (index: number, field: string, value: any) => {
    const next = (ladderLevels ?? []).map((l, i) =>
      i === index ? { ...l, [field]: value } : l
    );
    setLadderLevels(next);
  };

  const toggleSection = (id: SectionId) => {
    sectionsTouched.current = true;
    const next = { ...sections, [id]: !sections[id] };
    setSections(next);
    runPreview({ sections: next });
  };

  /**
   * Changing the offer type re-seeds the sections (the server overlays the
   * offer's shape), so the operator sees the consequence on the same screen.
   * Assumption overrides survive - they are the operator's, not the preset's.
   */
  const changeOfferType = (next: OfferType) => {
    setOfferType(next);
    setSections({});
    runPreview({ offerType: next, sections: {} });
  };

  const changeFacilityType = (next: FacilityType) => {
    setFacilityType(next);
    // The facility now also picks section defaults (dig outs and compliance
    // are off for non-playground and developer proposals), so untouched
    // toggles are re-derived like an offer-type change. Toggles the operator
    // set by hand are kept.
    if (!sectionsTouched.current) {
      setSections({});
      runPreview({ facilityType: next, sections: {} });
      return;
    }
    runPreview({ facilityType: next });
  };

  const commitAssumption = () => {
    if (!assumptionDraft) return;
    const def = Object.values(assumptionDefs).reduce<AssumptionDef[]>((acc, list) => acc.concat(list ?? []), []).find((d) => d.key === assumptionDraft.key);
    if (def?.requiresReason && !assumptionDraft.reason.trim()) return;
    const next = {
      ...assumptionOverrides,
      [assumptionDraft.key]: {
        value: def?.kind === "number" ? Number(assumptionDraft.value) : assumptionDraft.value,
        ...(assumptionDraft.reason.trim() ? { reason: assumptionDraft.reason.trim() } : {}),
      },
    };
    setAssumptionOverrides(next);
    setAssumptionDraft(null);
    runPreview({ assumptions: next });
  };

  const resetAssumption = (key: string) => {
    const next = { ...assumptionOverrides };
    delete next[key];
    setAssumptionOverrides(next);
    setAssumptionDraft(null);
    runPreview({ assumptions: next });
  };

  const proposalSummary = () => {
    const offer = OFFER_OPTIONS.find((o) => o.value === (offerType ?? payload?.offerType));
    const fac = FACILITY_OPTIONS.find((f) => f.value === facilityType);
    const who =
      customerType === "existing_client_portfolio" ? "an existing client"
      : customerType === "completed_site" ? "a site we have already mulched"
      : customerType === "single_location" ? "a single site"
      : "a new prospect";
    const n = payload?.portfolio?.totalLocations ?? sites.length;
    return `${offer ? offer.label : "A proposal"} for ${who}${fac && fac.value !== "other" ? ` (${fac.label.toLowerCase()})` : ""}, ${n} site${n === 1 ? "" : "s"}.`;
  };

  /** The assumption rows under one enabled section on step 3. */
  const renderAssumptions = (id: SectionId) => {
    const defs = assumptionDefs[id];
    if (!defs?.length || !sections[id]) return null;
    return (
      <div style={{ marginTop: 6, display: "flex", flexDirection: "column", gap: 4 }} onClick={(e) => e.stopPropagation()}>
        {defs.map((d) => {
          const v = payload?.assumptions?.[d.key];
          const editing = assumptionDraft?.key === d.key;
          const shown =
            d.kind === "choice" ? (d.choices?.find((c) => c.value === v?.value)?.label ?? String(v?.value ?? "")) : `${v?.value ?? "—"}${d.unit ? " " + d.unit : ""}`;
          return (
            <div key={d.key} style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 12, flexWrap: "wrap" }}>
              <span style={{ width: 240, color: "#4a4a4a" }}>{d.label}</span>
              {!editing && (
                <>
                  <b style={{ minWidth: 80 }}>{shown}</b>
                  <span className={`${styles.pill} ${v?.source === "measured" ? styles.info : v?.source === "operator" ? styles.warn : styles.ok}`}>
                    {SOURCE_LABELS[v?.source ?? "default"]}
                  </span>
                  {v?.detail && <span className={styles.sub} style={{ margin: 0 }}>{v.detail}</span>}
                  <button
                    type="button"
                    className={`${styles.btn} ${styles.ghost}`}
                    style={{ padding: "2px 8px", fontSize: 11 }}
                    onClick={() => setAssumptionDraft({ key: d.key, value: String(v?.value ?? ""), reason: v?.reason ?? "" })}
                  >
                    {d.requiresReason ? "Override (needs a reason)" : "Edit"}
                  </button>
                  {v?.source === "operator" && (
                    <button type="button" className={`${styles.btn} ${styles.ghost}`} style={{ padding: "2px 8px", fontSize: 11 }} onClick={() => resetAssumption(d.key)}>
                      Reset
                    </button>
                  )}
                </>
              )}
              {editing && assumptionDraft && (
                <>
                  {d.kind === "choice" ? (
                    <select value={assumptionDraft.value} onChange={(e) => setAssumptionDraft({ ...assumptionDraft, value: e.target.value })}>
                      {(d.choices ?? []).map((c) => (
                        <option key={c.value} value={c.value}>{c.label}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      style={{ width: 90 }}
                      value={assumptionDraft.value}
                      onChange={(e) => setAssumptionDraft({ ...assumptionDraft, value: e.target.value })}
                    />
                  )}
                  {d.requiresReason && (
                    <input
                      style={{ width: 320 }}
                      placeholder="Reason — kept on the proposal; client wording becomes 'estimated'"
                      value={assumptionDraft.reason}
                      onChange={(e) => setAssumptionDraft({ ...assumptionDraft, reason: e.target.value })}
                    />
                  )}
                  <button type="button" className={styles.btn} style={{ padding: "3px 10px", fontSize: 11 }} onClick={commitAssumption} disabled={busy || (d.requiresReason && !assumptionDraft.reason.trim())}>
                    Save
                  </button>
                  <button type="button" className={`${styles.btn} ${styles.ghost}`} style={{ padding: "3px 10px", fontSize: 11 }} onClick={() => setAssumptionDraft(null)}>
                    Cancel
                  </button>
                </>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  /* ---------------------------------------------------------------- step 4 */

  const loadHtml = async () => {
    setBusy(true);
    const res: any = await previewProposalHtml(buildInput);
    setBusy(false);
    if (res?.error) return setError(res.error);
    setHtml(res.html);
  };

  const saveAndSend = async (skipEmail = false) => {
    if (!payload) return;
    setBusy(true);
    setError(null);

    let id = savedId;
    if (!id) {
      const created: any = await createProposal(buildInput);
      if (created?.error) {
        setBusy(false);
        return setError(created.error);
      }
      id = created.id;
      setSavedId(id);
    } else {
      // A draft opened from the list may have had its sites, toggles or ladder
      // changed since it was saved. Persist that before freezing, or the
      // stored row and the sent document disagree.
      const updated: any = await updateProposal(id, buildInput);
      if (updated?.error) {
        setBusy(false);
        return setError(updated.error);
      }
    }

    const sent: any = await sendProposal(id as string, {
      payload,
      sentTo,
      subject: subject || undefined,
      message: messageBody,
      includePassword,
      skipEmail,
      requirePassword,
      password: requirePassword && customPassword.trim() ? customPassword.trim() : undefined,
    });
    setBusy(false);
    if (sent?.error) return setError(sent.error);
    setShareUrl(sent.shareUrl);
    setEmailed(!!sent.emailed);
    setLinkPassword(sent.passwordHint ?? null);
  };

  const savePassword = async (value: string | null) => {
    const id = savedId ?? editId;
    if (!id) return;
    setBusy(true);
    const res: any = await setProposalPassword(id, value);
    setBusy(false);
    if (res?.error) return setError(res.error);
    setLinkPassword(res.password ?? null);
    setPasswordDraft(null);
  };

  /* ----------------------------------------------------------------- render */

  const canLeaveStep1 = !!prospectName && !!productId && !!serviceId;
  const canLeaveStep2 = sites.length > 0;

  const STEP_HEADINGS: Array<{ title: string; subtitle: string }> = [
    {
      title: editId ? "Edit proposal" : "New proposal",
      subtitle:
        "Who this is for sets the template, which sections appear, and where the numbers come from.",
    },
    {
      title: prospectName || "New proposal",
      subtitle: "Which locations this proposal covers, and where their coordinates come from.",
    },
    {
      title: prospectName || "New proposal",
      subtitle:
        "Commitment levels and sections. Everything is pre-filled and reprices live as you change it.",
    },
    {
      title: prospectName || "New proposal",
      subtitle: "Read it the way the client will, then send it from here.",
    },
  ];

  const heading = STEP_HEADINGS[step - 1];

  return (
    <>
      <Header />
      <Page
        title={heading.title}
        subtitle={heading.subtitle}
        titleAction={
          <BasicButton outline scheme="secondary" keepText onClick={() => navigate("/proposals")}>
            {step === 1 ? "Cancel" : "Back to proposals"}
          </BasicButton>
        }
      >
        <div className={styles.wrap}>
      <div className={styles.steps}>
        {["1 · Who", "2 · Locations", "3 · Offer", "4 · Send"].map((label, i) => (
          <div
            key={label}
            className={step === i + 1 ? styles.on : step > i + 1 ? styles.done : undefined}
            // Every completed step is one click away - there was no way back
            // from Send except leaving the wizard and re-entering on step 3.
            style={{ cursor: i + 1 < step ? "pointer" : undefined }}
            onClick={() => {
              if (i + 1 < step) setStep(i + 1);
            }}
          >
            {label}
            {step > i + 1 ? " ✓" : ""}
          </div>
        ))}
      </div>

      {error && <div className={styles.err}>{error}</div>}

      {/* ---------------------------------------------------- STEP 1 */}
      {step === 1 && (
        <>
          <h2>Who is this proposal for?</h2>
          <p className={styles.sub}>
            This one choice sets the template, which sections appear, and where the numbers come
            from. Nothing later asks again.
          </p>
          <div className={styles.cards}>
            {TYPES.map((t) => (
              <button
                key={t.key}
                type="button"
                className={`${styles.card} ${customerType === t.key ? styles.sel : ""}`}
                onClick={() => setCustomerType(t.key)}
              >
                <h3>{t.title}</h3>
                <p>{t.body}</p>
                <div className={styles.eg}>{t.eg}</div>
              </button>
            ))}
          </div>

          <div className={styles.row}>
            <div className={styles.field}>
              <label>Prospect / client name</label>
              <input value={prospectName} onChange={(e) => setProspectName(e.target.value)} />
            </div>
            <div className={styles.field}>
              <label>From</label>
              <select value={senderUserId} onChange={(e) => setSenderUserId(e.target.value)}>
                <option value="">Me</option>
                {senders.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}{s.jobTitle ? ` · ${s.jobTitle}` : ""}
                  </option>
                ))}
              </select>
              <span className={styles.sub}>
                Their name, title, email and phone sign the document and receive its replies. Title and phone come from their profile (My Settings).
              </span>
            </div>
            <div className={styles.field}>
              <label>Prospect logo (optional)</label>
              {prospectLogoUrl ? (
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  {logoPreview || /^(https?:|data:image)/i.test(prospectLogoUrl) ? (
                    <img src={logoPreview || prospectLogoUrl} alt="" style={{ height: 36, maxWidth: 140, objectFit: "contain", background: "#fff", border: "1px solid #e3e3de", borderRadius: 6, padding: 4 }} />
                  ) : (
                    // A stored upload is a private storage key; the document
                    // preview shows it, this chip just says it is there.
                    <span style={{ fontSize: 12, padding: "4px 10px", border: "1px solid #e3e3de", borderRadius: 6, background: "#fff" }}>
                      Logo uploaded ({prospectLogoUrl.split("/").pop()})
                    </span>
                  )}
                  <button type="button" className={`${styles.btn} ${styles.ghost}`} style={{ padding: "3px 10px", fontSize: 11 }} onClick={() => { setProspectLogoUrl(""); setLogoPreview(""); }}>
                    Remove
                  </button>
                </div>
              ) : (
                <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                  {/* Uploads the moment a file is chosen - the old second
                      "Use this logo" click was easy to miss, and a logo that
                      showed its filename but was never saved looked done. */}
                  <FileInput
                    directory="proposals/logos"
                    refreshValue={logoBusy}
                    onChange={async (val: any) => {
                      const up = val?.[0];
                      if (!up?.url || !up?.file) return;
                      if (!String(up.file.type || "").startsWith("image/")) return setError("The logo must be an image (PNG, JPG, SVG).");
                      if (up.file.size > 500 * 1024) return setError("The logo must be under 500 KB.");
                      setLogoBusy(true);
                      try {
                        const r = await fetch(up.url, { method: "PUT", headers: { "Content-Type": up.file.type }, body: up.file });
                        if (r.ok) {
                          setProspectLogoUrl(up.location);
                          setLogoPreview(URL.createObjectURL(up.file));
                        } else {
                          setError("The logo upload failed.");
                        }
                      } catch {
                        setError("The logo upload failed.");
                      }
                      setLogoBusy(false);
                    }}
                  />
                  {logoBusy && <span style={{ fontSize: 12 }}>Uploading…</span>}
                </div>
              )}
              <span className={styles.sub}>Drawn beside ours at the top of the document and in the toolbar.</span>
            </div>
            {customerType === "existing_client_portfolio" && (
              <div className={styles.field}>
                <label>Client</label>
                <select
                  value={clientId}
                  onChange={(e) => {
                    setClientId(e.target.value);
                    // Their name is the proposal's name unless it has been
                    // typed over already.
                    const picked = clients.find((c) => c.id === e.target.value);
                    if (picked && !prospectName) setProspectName(picked.name);
                  }}
                >
                  <option value="">Select a client…</option>
                  {clients.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {leadsEnabled && customerType !== "existing_client_portfolio" && (
            <div className={styles.row}>
              <div className={styles.field} style={{ flex: 2 }}>
                <label>Lead</label>
                <LeadPicker
                  value={leadId || null}
                  suggestFor={prospectName}
                  placeholder={"Search leads by organization, contact or email \u2014 or leave unattached"}
                  onChange={(l) => {
                    const v = l?.id ?? "";
                    setLeadId(v);
                    setClientId(v);
                    setCreateLeadOnNext(false);
                    if (l && !prospectName) setProspectName(l.name);
                  }}
                />
                <span className={styles.sub}>
                  Attach it and every open, reply and follow-up shows on the lead&rsquo;s Activity.
                </span>
              </div>
              {!leadId && (
                <div className={styles.field} style={{ flex: 1, justifyContent: "flex-end" }}>
                  <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                    <input
                      type="checkbox"
                      checked={createLeadOnNext}
                      onChange={(e) => setCreateLeadOnNext(e.target.checked)}
                      style={{ width: 16 }}
                    />
                    Create a new lead from &ldquo;{prospectName || "the prospect name"}&rdquo;
                  </label>
                  <span className={styles.sub}>Made when you press Next, owned by you, source &ldquo;proposal generator&rdquo;.</span>
                </div>
              )}
            </div>
          )}

          <div className={styles.row}>
            <div className={styles.field}>
              <label>Product</label>
              <select value={productId} onChange={(e) => setProductId(e.target.value)}>
                <option value="">Select a product…</option>
                {catalog.products.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                    {p.unit ? ` (${p.unit})` : ""}
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.field}>
              <label>Service</label>
              <select value={serviceId} onChange={(e) => setServiceId(e.target.value)}>
                <option value="">Select a service…</option>
                {catalog.services.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name} — {s.depots} depots
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className={styles.foot}>
            <span className={styles.cost}>Step 1 of 4</span>
            <button
              className={styles.btn}
              disabled={!canLeaveStep1 || busy}
              onClick={async () => {
                if (createLeadOnNext && !leadId) {
                  setBusy(true);
                  setError(null);
                  const made: any = await createLead({ name: prospectName.trim(), leadSource: "proposal_generator" });
                  setBusy(false);
                  if (made?.error) return setError(made.error);
                  setLeadId(made.id);
                  setClientId(made.id);
                  setCreateLeadOnNext(false);
                }
                setStep(2);
              }}
            >
              Next — locations
            </button>
          </div>
        </>
      )}

      {/* ---------------------------------------------------- STEP 2 */}
      {step === 2 && (
        <>
          <h2>Which locations?</h2>
          <p className={styles.sub}>
            The input mode is preselected by step 1. The others stay available.
          </p>

          <div className={styles.tabs}>
            <div className={mode === "paste" ? styles.on : undefined} onClick={() => setMode("paste")}>
              Paste a list
            </div>
            <div className={mode === "csv" ? styles.on : undefined} onClick={() => setMode("csv")}>
              Upload CSV
            </div>
            <div className={mode === "ours" ? styles.on : undefined} onClick={() => setMode("ours")}>
              From our data
            </div>
          </div>

          {mode === "paste" && (
            <>
              <textarea
                value={pasteText}
                onChange={(e) => setPasteText(e.target.value)}
                placeholder={"Childcare Network #7012 | Dothan, AL\n31.2232, -85.3905, Enterprise, AL\nChildcare Network #7330 | Mobile, AL, 180"}
              />
              <p className={styles.sub} style={{ margin: "8px 0 0" }}>
                Accepts <code>City, ST</code> · <code>lat, lng, City, ST</code> ·{" "}
                <code>Name | City, ST</code> — add a yards override with a trailing number.
              </p>
              <div style={{ marginTop: 12 }}>
                <button className={styles.btn} onClick={runParse} disabled={busy || !pasteText.trim()}>
                  {busy ? "Reading…" : "Read the list"}
                </button>
              </div>
            </>
          )}

          {mode === "csv" && (
            <div style={{ marginTop: 4 }}>
              <input
                type="file"
                accept=".csv,text/csv"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  setBusy(true);
                  setError(null);
                  const text = await file.text();
                  const res: any = await parseLocationList({ csv: text });
                  setBusy(false);
                  if (res?.error) return setError(res.error);
                  if (!res.rows?.length) {
                    return setError(
                      "Nothing readable in that file. It needs a header row with either city and state, or lat and lng.",
                    );
                  }
                  setRows(res.rows);
                  setSummary(res.summary);
                  setDuplicates(res.duplicates ?? []);
                  replaceFromMode(
                    "csv-",
                    res.rows
                      .filter((r: ParsedRow) => r.latitude != null && r.longitude != null)
                      .map((r: ParsedRow, i: number) => ({
                        ref: `csv-${r.sourceRow}-${i}`,
                        name: r.name,
                        state: r.state,
                        latitude: r.latitude as number,
                        longitude: r.longitude as number,
                        yards: r.yards ?? undefined,
                      })),
                    true,
                  );
                }}
              />
              <p className={styles.sub} style={{ margin: "10px 0 0" }}>
                Needs a header row. Address columns are read under their usual names -{" "}
                <code>address</code>/<code>street</code>, <code>address 2</code>/<code>suite</code>,{" "}
                <code>city</code>, <code>state</code>, <code>zip</code>/<code>postal code</code>, or one{" "}
                <code>full address</code> column - plus <code>name</code>/<code>location name</code>,{" "}
                <code>lat</code>/<code>lng</code> and <code>yards</code>. A street address is what
                lets a site be placed exactly; city and state alone cannot be priced. Rows that
                share a street address are merged into one site, and you are told which.
              </p>
            </div>
          )}

          {mode === "ours" && (
            <div style={{ marginTop: 4 }}>
              <button className={styles.btn} onClick={loadFromOurData} disabled={busy}>
                {busy ? "Loading…" : customerType === "existing_client_portfolio" ? "Load their portfolio" : "Load completed projects"}
              </button>
            </div>
          )}

          {candidates.length > 0 && (
            <>
              <h2 style={{ marginTop: 26, fontSize: 14 }}>
                {candidates.length} found &middot; {sites.length} selected
              </h2>
              <p className={styles.sub}>
                Tick the sites this proposal should cover. Sites add up across the tabs, so a
                client&rsquo;s known locations and a pasted list of new ones can go in together.
                Contact answers already on file cost nothing and are loaded; only unresolved ones
                bill, and the price is on the button.
              </p>
              <div style={{ marginBottom: 12, display: "flex", gap: 8 }}>
                <button
                  type="button"
                  className={`${styles.btn} ${styles.ghost}`}
                  onClick={() => setSelectedRefs(new Set(candidates.map((c) => c.ref)))}
                >
                  Select all
                </button>
                <button
                  type="button"
                  className={`${styles.btn} ${styles.ghost}`}
                  onClick={() => setSelectedRefs(new Set())}
                >
                  Deselect all
                </button>
                <button
                  type="button"
                  className={`${styles.btn} ${styles.ghost}`}
                  onClick={clearCandidates}
                >
                  Start over
                </button>
              </div>
              <table>
                <thead>
                  <tr>
                    <th style={{ width: 32 }} />
                    <th>Site</th>
                    <th>State</th>
                    <th>Yards</th>
                    <th>History</th>
                    <th>End customer</th>
                    <th>Contact</th>
                  </tr>
                </thead>
                <tbody>
                  {candidates.slice(candPage * ROWS_PER_PAGE, (candPage + 1) * ROWS_PER_PAGE).map((s) => {
                    const c = contacts[s.ref];
                    const on = selectedRefs.has(s.ref);
                    return (
                      <tr
                        key={s.ref}
                        onClick={() => toggleSite(s.ref)}
                        style={{ cursor: "pointer", opacity: on ? 1 : 0.55 }}
                      >
                        <td>
                          <input type="checkbox" checked={on} readOnly style={{ width: 16 }} />
                        </td>
                        <td>{s.name}</td>
                        <td>{s.state ?? "\u2014"}</td>
                        <td>{s.yards ? Math.round(s.yards) : "\u2014"}</td>
                        <td style={{ fontSize: 11, color: "#818181" }}>
                          {(s as any).projectCount
                            ? `${(s as any).projectCount} project${
                                (s as any).projectCount === 1 ? "" : "s"
                              }${
                                (s as any).lastServiced
                                  ? `, last ${new Date((s as any).lastServiced).getFullYear()}`
                                  : ""
                              }`
                            : "never serviced"}
                        </td>
                        <td>
                          {c?.endCustomerName ?? (
                            <span className={`${styles.pill} ${styles.warn}`}>not identified</span>
                          )}
                        </td>
                        <td style={{ fontSize: 11 }}>
                          {c?.phone ?? "\u2014"}
                          {c?.website ? " \u00b7 website" : ""}
                          {c && !c.email && <span style={{ color: "#ba7517" }}> · no email</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {candidates.length > ROWS_PER_PAGE && (
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 10 }}>
                  <button className={`${styles.btn} ${styles.ghost}`} disabled={candPage === 0} onClick={() => setCandPage((p) => Math.max(p - 1, 0))}>
                    Previous
                  </button>
                  <span className={styles.sub} style={{ margin: 0 }}>
                    {candPage * ROWS_PER_PAGE + 1}–{Math.min((candPage + 1) * ROWS_PER_PAGE, candidates.length)} of {candidates.length} · Select all and Deselect all cover every page
                  </span>
                  <button className={`${styles.btn} ${styles.ghost}`} disabled={(candPage + 1) * ROWS_PER_PAGE >= candidates.length} onClick={() => setCandPage((p) => p + 1)}>
                    Next
                  </button>
                </div>
              )}

              {billableSites.length > 0 && (
                <div className={styles.note}>
                  <b>
                    {sites.length - billableSites.length} of the selected sites already have a
                    contact on file, {billableSites.length} do not.
                  </b>{" "}
                  Looking those up costs about ${(billableSites.length * 0.032).toFixed(2)}, and the
                  answers are kept so the next proposal touching these sites is free.
                  <div style={{ marginTop: 10 }}>
                    <button className={styles.btn} onClick={runEnrichment} disabled={busy}>
                      {busy
                        ? "Looking up…"
                        : `Find contacts — ${billableSites.length} sites · $${(
                            billableSites.length * 0.032
                          ).toFixed(2)}`}
                    </button>
                  </div>
                </div>
              )}
            </>
          )}

          {summary && (
            <>
              <h2 style={{ marginTop: 26, fontSize: 14 }}>
                {summary.read && summary.read > summary.total
                  ? `${summary.read} rows read · ${summary.total} sites`
                  : `${summary.total} locations read`}{" "}
                · {summary.withCoordinates} ready to price · {summary.flagged} need attention
              </h2>
              {duplicates.length > 0 && (
                <div className={styles.note}>
                  <b>
                    {duplicates.reduce((n, d) => n + d.dropped.length, 0)} rows shared a street address
                    with another row and were merged, so {duplicates.length}{" "}
                    {duplicates.length === 1 ? "address is" : "addresses are"} priced once each.
                  </b>{" "}
                  Two names at one address is one site with one delivery; pricing both would double the
                  yards and inflate the location count the offer is built on. If any of these really
                  are separate sites, the list has the wrong address for them.
                  <div style={{ marginTop: 10 }}>
                    <button
                      className={`${styles.btn} ${styles.ghost}`}
                      onClick={() => setShowDuplicates((v) => !v)}
                    >
                      {showDuplicates ? "Hide merged rows" : "Show merged rows"}
                    </button>
                  </div>
                  {showDuplicates && (
                    <table style={{ marginTop: 10 }}>
                      <thead>
                        <tr>
                          <th>Address</th>
                          <th>Kept</th>
                          <th>Merged in</th>
                        </tr>
                      </thead>
                      <tbody>
                        {duplicates.map((d, i) => (
                          <tr key={i}>
                            <td>
                              {d.address}, {d.city}, {d.state}
                            </td>
                            <td>
                              {d.kept.name} <span className={styles.sub}>(row {d.kept.sourceRow})</span>
                            </td>
                            <td>
                              {d.dropped.map((x) => (
                                <div key={x.sourceRow}>
                                  {x.name} <span className={styles.sub}>(row {x.sourceRow})</span>
                                </div>
                              ))}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
              {summary.needGeocode > 0 && (
                <div className={styles.note}>
                  <b>{summary.needGeocode} rows have no coordinates and cannot be priced yet.</b> A
                  site priced against whatever depot is nearest a city centroid is how a proposal
                  goes out with the wrong number on it, so these are held back rather than guessed.
                  <div style={{ marginTop: 10 }}>
                    <button className={styles.btn} onClick={runGeocode} disabled={busy}>
                      {busy ? (geocodeProgress ? `Looking up\u2026 ${geocodeProgress}` : "Looking up\u2026") : `Find coordinates for ${summary.needGeocode} rows`}
                    </button>
                  </div>
                </div>
              )}
              {tableRows.length > 0 && (
                <>
                {tableRows.length < rows.length && (
                  <p className={styles.sub} style={{ marginTop: 14 }}>
                    The {rows.length - tableRows.length} placed sites are in the list above. Only the {tableRows.length} that need attention are shown here.
                  </p>
                )}
                <table style={{ marginTop: 14 }}>
                  <thead>
                    <tr>
                      <th>Location</th>
                      <th>Coords</th>
                      <th>Yards</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tableRows.slice(rowsPage * ROWS_PER_PAGE, (rowsPage + 1) * ROWS_PER_PAGE).map((r, i) => (
                      <tr key={i} className={r.flags.length ? styles.flagged : undefined}>
                        <td>
                          {r.name}
                          {r.flags.includes("no_state") && (
                            <span className={`${styles.pill} ${styles.warn}`}> no state</span>
                          )}
                          {!!(r.address || r.alsoKnownAs?.length) && (
                            <div className={styles.sub} style={{ margin: 0 }}>
                              {r.address ? [r.address, r.city, [r.state, r.zip].filter(Boolean).join(" ")].filter(Boolean).join(", ") : null}
                              {r.alsoKnownAs?.length ? ` · also listed as ${r.alsoKnownAs.join("; ")}` : null}
                            </div>
                          )}
                        </td>
                        <td>
                          {r.latitude != null ? `${r.latitude.toFixed(2)}, ${r.longitude?.toFixed(2)}` : "—"}
                          {r.flags.includes("duplicate_coords") && (
                            <span className={`${styles.pill} ${styles.warn}`}> duplicate</span>
                          )}
                        </td>
                        <td>{r.yards ?? "—"}</td>
                        <td>
                          {r.flags.includes("needs_geocode") ? (
                            <span className={`${styles.pill} ${styles.bad}`}>needs geocode</span>
                          ) : (
                            <span className={`${styles.pill} ${styles.ok}`}>ready</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </>
              )}
              {tableRows.length > ROWS_PER_PAGE && (
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 10 }}>
                  <button
                    className={`${styles.btn} ${styles.ghost}`}
                    disabled={rowsPage === 0}
                    onClick={() => setRowsPage((p) => Math.max(p - 1, 0))}
                  >
                    Previous
                  </button>
                  <span className={styles.sub} style={{ margin: 0 }}>
                    {rowsPage * ROWS_PER_PAGE + 1}–{Math.min((rowsPage + 1) * ROWS_PER_PAGE, tableRows.length)} of{" "}
                    {tableRows.length}
                  </span>
                  <button
                    className={`${styles.btn} ${styles.ghost}`}
                    disabled={(rowsPage + 1) * ROWS_PER_PAGE >= tableRows.length}
                    onClick={() => setRowsPage((p) => p + 1)}
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}

          <div className={styles.foot}>
            <button className={`${styles.btn} ${styles.ghost}`} onClick={() => setStep(1)}>
              Back
            </button>
            <button
              className={styles.btn}
              // Not while coordinates are still being found: moving on
              // mid-lookup priced the first batch and dropped the rest.
              disabled={!canLeaveStep2 || busy}
              title={busy ? "Wait for the coordinate lookup to finish" : undefined}
              onClick={() => {
                setStep(3);
                seedSections(sites.length);
                runPreview();
              }}
            >
              {busy && geocodeProgress ? `Finding coordinates… ${geocodeProgress}` : "Next — the offer"}
            </button>
          </div>
        </>
      )}

      {/* ---------------------------------------------------- STEP 3 */}
      {step === 3 && (
        <>
          <h2>{payload ? proposalSummary() : "The offer"}</h2>
          <p className={styles.sub}>
            What kind of proposal this is, which sections it has, and the assumptions each one
            rests on. Change the type and the sections re-seed; anything you set yourself is kept.
          </p>

          <div className={styles.row} style={{ alignItems: "flex-start", gap: 16, marginBottom: 12 }}>
            <div className={styles.field} style={{ flex: 2 }}>
              <label>What are we offering?</label>
              <select value={offerType ?? payload?.offerType ?? ""} onChange={(e) => changeOfferType(e.target.value as OfferType)}>
                {OFFER_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              <span className={styles.sub}>
                {OFFER_OPTIONS.find((o) => o.value === (offerType ?? payload?.offerType))?.note ?? "Suggested from the site count."}
              </span>
            </div>
            <div className={styles.field} style={{ flex: 1 }}>
              <label>What kind of facilities?</label>
              <select value={facilityType} onChange={(e) => changeFacilityType(e.target.value as FacilityType)}>
                {facilityOptions.map((f) => (
                  <option key={f.value} value={f.value}>{f.label}</option>
                ))}
              </select>
              <span className={styles.sub}>Changes wording and default assumptions, never the maths.</span>
            </div>
          </div>

          <h2 style={{ fontSize: 14 }}>Commitment levels</h2>
          <p className={styles.sub}>
            Pre-filled from portfolio size and the ladder every shipped proposal has used. The
            proposal opens on the top level — the client sees the best case first and steps down.
          </p>

          {warnings && warnings.unpricedCount > 0 && (
            <div className={styles.note}>
              <b>{warnings.unpricedCount} locations could not be priced</b> and are excluded from
              every figure below. {warnings.unpriced.slice(0, 3).map((u) => u.name).join(", ")}
              {warnings.unpriced.length > 3 ? "…" : ""}
            </div>
          )}
          {warnings && warnings.pricedOnRepairedCoordinates > 0 && (
            <div className={styles.note}>
              <b>{warnings.pricedOnRepairedCoordinates} location{warnings.pricedOnRepairedCoordinates === 1 ? "" : "s"} priced off a repaired depot
              coordinate.</b> Those depots have latitude and longitude stored the wrong way round;
              the rate is corrected at read time, but the underlying data is still wrong.
            </div>
          )}

          {payload?.ladder?.levels?.length ? (
            <table className={styles.ladder}>
              <thead>
                <tr>
                  <th>Level</th>
                  <th>Locations</th>
                  <th>Discount %</th>
                  <th>Dig-outs</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {(ladderLevels ?? payload.ladder.levels).map((l: any, i: number) => (
                  <tr key={i} className={i === payload.ladder.openOnIndex ? styles.lvlDefault : undefined}>
                    <td>
                      {i + 1}
                      {i === (ladderLevels ?? payload.ladder.levels).length - 1 &&
                        l.locations >= payload.portfolio.totalLocations && (
                          <span className={styles.sub} style={{ margin: 0 }}> · All sites</span>
                        )}
                    </td>
                    <td>
                      <input
                        value={l.locations}
                        onChange={(e) => editLevel(i, "locations", Number(e.target.value) || 0)}
                        onBlur={() => runPreview()}
                      />
                    </td>
                    <td>
                      <input
                        value={Math.round(l.discount * 100)}
                        onChange={(e) =>
                          editLevel(i, "discount", (Number(e.target.value) || 0) / 100)
                        }
                        onBlur={() => runPreview()}
                      />
                    </td>
                    <td>
                      <div
                        className={`${styles.sw} ${l.digOuts ? styles.on : ""}`}
                        onClick={() => {
                          editLevel(i, "digOuts", !l.digOuts);
                          setTimeout(() => runPreview(), 0);
                        }}
                      >
                        <i />
                      </div>
                    </td>
                    <td>
                      {i === payload.ladder.openOnIndex && (
                        <span className={`${styles.pill} ${styles.info}`}>opens here</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className={styles.sub}>
              Under 40 locations — no commitment framing. Nobody deliberates over three sites.
            </p>
          )}

          <p className={styles.sub} style={{ marginTop: 10 }}>
            Counts and discounts are pre-filled from portfolio size and the ladder every shipped
            proposal has used (5 / 12 / 16 / 20%). Change any cell — it reprices when you leave
            the field. <b>Dig-outs are a per-level switch</b>, not a global threshold: leave every
            level off and the section disappears.
          </p>

          <h2 style={{ fontSize: 14, marginTop: 24 }}>Sections</h2>
          <p className={styles.sub}>
            Defaults come from the customer type. Toggles change which sections appear, never what
            they say.
            {!payload && busy && (
              <b style={{ display: "block", marginTop: 4 }}>
                {`Pricing ${sites.length} site${sites.length === 1 ? "" : "s"} \u2014 the numbers fill in when it finishes.`}
              </b>
            )}
          </p>
          <div className={styles.toggles}>
            {(customerType === "single_location" || customerType === "completed_site"
              ? MAINTENANCE_SECTIONS
              : PORTFOLIO_SECTIONS
            ).map((id) => (
              <div key={id} className={styles.tg} onClick={() => toggleSection(id)}>
                <div className={`${styles.sw} ${sections[id] ? styles.on : ""}`}>
                  <i />
                </div>
                <div>
                  <b>{SECTION_LABELS[id]?.label}</b>
                  <span>{SECTION_LABELS[id]?.note}</span>
                  {renderAssumptions(id)}
                </div>
              </div>
            ))}
          </div>

          {sections.emergency_response && (
            <div className={styles.note}>
              {caseStudies.length === 0 ? (
                <>
                  <b>No approved case study yet, so this section will not appear.</b> A case study
                  is measured from completed work over a window you choose, then approved —
                  figures nobody has read do not go on a document.
                </>
              ) : (
                <>
                  <b>Which case study?</b>
                  <div className={styles.field} style={{ maxWidth: 440, marginTop: 8 }}>
                    <select value={caseStudyId} onChange={(e) => setCaseStudyId(e.target.value)}>
                      <option value="">None — leave the section out</option>
                      {caseStudies.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name} &middot; {c.stats?.locations ?? 0} locations,{" "}
                          {Math.round(c.stats?.yards ?? 0).toLocaleString()} yards
                        </option>
                      ))}
                    </select>
                  </div>
                  {caseStudyId && (
                    <div style={{ marginTop: 6, fontSize: 11 }}>
                      {caseStudies.find((c) => c.id === caseStudyId)?.driftNote
                        ? `A recent check found these figures have moved: ${
                            caseStudies.find((c) => c.id === caseStudyId)?.driftNote
                          }`
                        : "Measured from completed work and approved."}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {payload && (
            <table style={{ marginTop: 22 }}>
              <thead>
                <tr>
                  <th>Region</th>
                  <th>Sites</th>
                  <th>Priced</th>
                  <th>Standard $/yd</th>
                  <th>Range</th>
                  <th>Avg yards</th>
                </tr>
              </thead>
              <tbody>
                {payload.regions.map((r) => (
                  <tr key={r.key}>
                    <td>
                      <span
                        style={{
                          display: "inline-block",
                          width: 12,
                          height: 12,
                          borderRadius: 3,
                          background: r.color,
                          marginRight: 6,
                          verticalAlign: "middle",
                        }}
                      />
                      {r.name}
                    </td>
                    <td>{r.locationCount}</td>
                    <td>
                      {r.pricedLocations < r.locationCount ? (
                        <span className={`${styles.pill} ${styles.warn}`}>
                          {r.pricedLocations}/{r.locationCount}
                        </span>
                      ) : (
                        r.pricedLocations
                      )}
                    </td>
                    <td>${r.baseRate.toFixed(2)}</td>
                    <td>
                      ${r.minRate.toFixed(2)}–${r.maxRate.toFixed(2)}
                    </td>
                    <td>{r.avgYards || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {error && <div className={styles.err} style={{ marginTop: 16 }}>{error}</div>}

          <div className={styles.foot}>
            <button className={`${styles.btn} ${styles.ghost}`} onClick={() => setStep(2)}>
              Back
            </button>
            <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
              {savedAt && (
                <span style={{ fontSize: 11, color: "#818181" }}>
                  Saved {savedAt.toLocaleTimeString()}
                </span>
              )}
              <button
                className={`${styles.btn} ${styles.ghost}`}
                disabled={busy || sites.length === 0}
                onClick={() => saveDraftOnly()}
              >
                Save draft
              </button>
              <button
                className={styles.btn}
                disabled={!payload || busy}
                onClick={() => {
                  setStep(4);
                  loadHtml();
                }}
              >
                Next — review and send
              </button>
            </span>
          </div>
        </>
      )}

      {/* ---------------------------------------------------- STEP 4 */}
      {step === 4 && (
        <>
          <h2>Preview</h2>
          <p className={styles.sub}>This is what the client opens. Every step in the bar above is a link back.</p>
          <div style={{ display: "flex", gap: 18, alignItems: "flex-start" }}>
          <div style={{ flex: 1, minWidth: 0 }}>
          {busy && !html ? (
            <p className={styles.sub}>Rendering…</p>
          ) : (
            <iframe className={styles.previewFrame} title="Proposal preview" srcDoc={html} />
          )}
          </div>
          <div style={{ width: 400, flexShrink: 0, position: "sticky", top: 16 }}>
          <h2 style={{ marginTop: 0 }}>Send it</h2>
          <p className={styles.sub} style={{ marginTop: 0 }}>
            From <b>{senders.find((s) => s.id === senderUserId)?.name ?? "me"}</b> — that is the name, email and phone on the document. Change it on step 1.
          </p>
          <p className={styles.sub}>
            A <b>View proposal</b> button sits above your message and a plain link repeats below
            it &mdash; you do not paste either in. Getting the click is the whole job of this
            email, so the ask does not wait until the end.
          </p>
          <div className={styles.row}>
            <div className={styles.field}>
              <label>To</label>
              <input value={sentTo} onChange={(e) => setSentTo(e.target.value)} />
            </div>
            <div className={styles.field}>
              <label>Subject</label>
              <input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder={payload ? `${payload.prospectName} — preliminary pricing` : ""}
              />
            </div>
          </div>
          <div className={styles.row}>
            <div className={styles.field}>
              <label>Message</label>
              <MessageEditor
                value={messageBody}
                onChange={setMessageBody}
                minHeight={200}
                email={{
                  shareUrl,
                  password: linkPassword || customPassword.trim() || null,
                  hasPassword: requirePassword,
                  includePassword,
                  fromName: senders.find((s) => s.id === senderUserId)?.name ?? "GetMulch.com",
                  replyTo: senders.find((s) => s.id === senderUserId)?.email ?? null,
                  subject: subject || (payload ? `${payload.prospectName} \u2014 preliminary pricing` : null),
                  to: sentTo || null,
                }}
              />
            </div>
          </div>

          {!shareUrl && (
            <>
              <div className={styles.tg} onClick={() => setRequirePassword(!requirePassword)}>
                <div className={`${styles.sw} ${requirePassword ? styles.on : ""}`}>
                  <i />
                </div>
                <div>
                  <b>Require a password to open the link</b>
                  <span>
                    A second layer on an already unguessable link. Off means the link alone opens it.
                  </span>
                </div>
              </div>
              {requirePassword && (
                <div className={styles.field} style={{ maxWidth: 360 }}>
                  <label>Password (leave blank for the generated one)</label>
                  <input
                    value={customPassword}
                    placeholder="e.g. CADENCE-2027"
                    onChange={(e) => setCustomPassword(e.target.value)}
                  />
                  <span className={styles.sub}>Letters, digits, - _ . only, 4–40 characters.</span>
                </div>
              )}
            </>
          )}

          {requirePassword && !shareUrl && (
          <div className={styles.tg} onClick={() => setIncludePassword(!includePassword)}>
            <div className={`${styles.sw} ${includePassword ? styles.on : ""}`}>
              <i />
            </div>
            <div>
              <b>Include the password in the email</b>
              <span>
                Off sends it separately to the same person. Slower, but a forwarded email no longer
                carries both halves.
              </span>
            </div>
          </div>
          )}

          {shareUrl && (
            <div className={styles.note}>
              <b>{emailed ? "Sent." : "Link created — nothing was emailed."}</b>{" "}
              <code>{shareUrl}</code>
              <button type="button" className={`${styles.btn} ${styles.ghost}`} style={{ padding: "2px 8px", fontSize: 11, marginLeft: 6 }} onClick={() => navigator.clipboard?.writeText(shareUrl).catch(() => setError("Could not copy — select it and copy by hand."))}>
                Copy link
              </button>
              <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <span>
                  Password: <b>{linkPassword ?? "none — the link alone opens it"}</b>
                </span>
                {linkPassword && (
                  <button type="button" className={`${styles.btn} ${styles.ghost}`} style={{ padding: "2px 8px", fontSize: 11 }} onClick={() => navigator.clipboard?.writeText(linkPassword).catch(() => setError("Could not copy — select it and copy by hand."))}>
                    Copy password
                  </button>
                )}
                {passwordDraft === null ? (
                  <>
                    <button className={`${styles.btn} ${styles.ghost}`} style={{ padding: "3px 10px", fontSize: 11 }} onClick={() => setPasswordDraft(linkPassword ?? "")}>
                      {linkPassword ? "Change" : "Add one"}
                    </button>
                    {linkPassword && (
                      <button className={`${styles.btn} ${styles.ghost}`} style={{ padding: "3px 10px", fontSize: 11 }} disabled={busy} onClick={() => savePassword(null)}>
                        Remove
                      </button>
                    )}
                  </>
                ) : (
                  <>
                    <input style={{ width: 200 }} value={passwordDraft} onChange={(e) => setPasswordDraft(e.target.value)} />
                    <button className={styles.btn} style={{ padding: "3px 10px", fontSize: 11 }} disabled={busy || passwordDraft.trim().length < 4} onClick={() => savePassword(passwordDraft.trim())}>
                      Save
                    </button>
                    <button className={`${styles.btn} ${styles.ghost}`} style={{ padding: "3px 10px", fontSize: 11 }} onClick={() => setPasswordDraft(null)}>
                      Cancel
                    </button>
                  </>
                )}
              </div>
              {!emailed && (
                <div style={{ marginTop: 6 }}>
                  Because you copied it rather than sending from here, the app knows only that a
                  link exists — not that anything was sent. The follow-up row cannot work for this
                  one.
                </div>
              )}
              {savedId && (
                <div style={{ marginTop: 10 }}>
                  <button
                    className={`${styles.btn} ${styles.ghost}`}
                    onClick={() => navigate(`/proposals/${savedId}`)}
                  >
                    Open the proposal record
                  </button>
                </div>
              )}
            </div>
          )}

          {/* The page-level error banner sits at the top; on this screen the
              operator is at the bottom next to the buttons, so a failed send
              rendered offscreen and read as "nothing happened". */}
          {error && <div className={styles.err} style={{ marginTop: 16 }}>{error}</div>}
          </div>
          </div>

          <div className={styles.foot}>
            <button className={`${styles.btn} ${styles.ghost}`} onClick={() => setStep(3)}>
              Back
            </button>
            <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {savedAt && (
                <span style={{ fontSize: 11, color: "#818181", marginRight: 4 }}>
                  Saved {savedAt.toLocaleTimeString()}
                </span>
              )}
              <button
                className={`${styles.btn} ${styles.ghost}`}
                disabled={busy || !!shareUrl}
                onClick={() => saveDraftOnly()}
              >
                Save draft
              </button>
              <button
                className={styles.btn}
                disabled={busy || !!shareUrl}
                onClick={() => saveAndSend(true)}
              >
                {busy ? "Working…" : "Save & create link"}
              </button>
              <button
                className={`${styles.btn} ${styles.ghost}`}
                disabled={busy || !sentTo || !!shareUrl}
                onClick={() => saveAndSend(false)}
              >
                Send by email
              </button>
            </span>
          </div>
        </>
      )}
        </div>
      </Page>
    </>
  );
};

export default Proposals;
